export interface VariablePriceBarcodeResult {
  wbpId: string;
  priceInCents: number;
}

/**
 * Result of parsing a weight-based pricing (WBP) barcode.
 * Extends the raw barcode parse result with the source barcode string.
 */
export interface WeightBasedPricingEntry {
  sku: string;
  wbpId: string;
  priceInCents: number;
  barcode: string;
}

export type DuplicateWeightResolution = "ADD_WITH_SAME_WEIGHT" | "TRY_AGAIN";

const VARIABLE_PRICE_PREFIXES = ["21", "22", "27"];

/**
 * Parse an EAN-13 barcode for variable-price items.
 *
 * Validation:
 *   1. Barcode must be exactly 13 numeric digits
 *   2. Must start with a variable-price prefix (21, 22, or 27)
 *
 * Structure:
 *   digits 1-6:  wbp_id (prefix + PLU)
 *   digit 7:     price check digit (ignored)
 *   digits 8-12: price in cents
 *   digit 13:    EAN-13 check digit (ignored)
 *
 * Returns null if validation fails.
 */
export function parseVariablePriceBarcode(barcode: string): VariablePriceBarcodeResult | null {
  if (barcode.length !== 13 || /[^0-9]/.test(barcode)) {
    return null;
  }

  const prefix = barcode.substring(0, 2);
  if (!VARIABLE_PRICE_PREFIXES.includes(prefix)) {
    return null;
  }

  const wbpId = barcode.substring(0, 6);
  const priceInCents = parseInt(barcode.substring(7, 12), 10);

  return { wbpId, priceInCents };
}

/**
 * Check whether a new entry duplicates the price of any already-scanned
 * entry for the same item.
 */
export function findDuplicateWeight(
  newEntry: WeightBasedPricingEntry,
  existingEntries: readonly { priceInCents: number }[],
): boolean {
  return existingEntries.some((entry) => entry.priceInCents === newEntry.priceInCents);
}
